/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : convertToNeuDsl.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
export function convertToNeuDSL(nodes, edges) {
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const outgoing = {};

  for (const e of edges) {
    outgoing[e.source] ||= [];
    outgoing[e.source].push(e);
  }

  const start = nodes.find((n) => n.type === "start");
  const dslNodes = {};

  const isEndNode = (id) => nodeMap[id]?.type === "end";
  const displayId = (id) => `${id}_display`;

  function build(nodeId) {
    if (!nodeId || dslNodes[nodeId]) return;

    const node = nodeMap[nodeId];
    const outs = outgoing[nodeId] || [];

    switch (node.type) {
      case "start": {
        const next = outs[0]?.target;

        dslNodes[nodeId] = {
          Type: "Forward",
          GoTo: isEndNode(next) ? next : displayId(next),
        };

        build(next);
        break;
      }

      case "question": {
        const displayIds = `${nodeId}_display`;
        const decisionId = `${nodeId}_decision`;

        dslNodes[displayIds] = {
          Type: "Action",
          Display: {
            showToUser: true, // ← REQUIRED
            Label: node.data.questionText,
          },
          Label: node.data.questionText,
          Render: {
            component: "question",
            fields: node.data.answers.map((a) => ({
              id: a.id,
              label: a.label,
              type: "radio",
            })),
          },
          Output: {
            contextKeys: [`${nodeId}_answer`],
          },
          GoTo: decisionId,
        };

        const choices = node.data.answers.map((a) => {
          const edge = outs.find((e) => e.sourceHandle === a.id);
          let goTo = null;
          if (edge) {
            goTo = isEndNode(edge.target) ? edge.target : displayId(edge.target);
          }

          return {
            Label: a.label,
            Condition: `input.${nodeId}_answer == "${a.id}"`, // ← REQUIRED
            GoTo: goTo,
          };
        });

        dslNodes[decisionId] = {
          Type: "Decision",
          Choices: choices,
        };

        for (const c of choices) {
          if (c.GoTo) {
            build(c.GoTo.replace("_display", ""));
          }
        }
        break;
      }

      case "end": {
        dslNodes[nodeId] = {
          Type: "Complete",
          Output: {
            outcome: node.data.outcome.value, // CREATE_TICKET | NO_TICKET
          },
        };
        break;
      }
    }
  }

  build(start.id);

  return {
    EntryNode: start.id,
    Nodes: dslNodes,
  };
}
